import React, { useMemo } from "react";
import {
  Paper,
  Group,
  Select,
  Button,
  Menu,
  ActionIcon,
  Loader,
  Text,
  Badge,
  SimpleGrid,
  Divider,
  Avatar,
  Tooltip,
  Box,
  Container,
} from "@mantine/core";
import {
  IconPlus,
  IconChevronRight,
  IconEdit,
  IconTrash,
  IconBuildingCommunity,
  IconMail,
  IconPhone,
  IconUsers,
  IconActivity,
  IconAlertCircle,
} from "@tabler/icons-react";
import { FcExpand } from "react-icons/fc";
import { BenchTable } from "../pages/UI";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import BenchStatCard from "../UIComponent/Components/BenchStatCard";

interface TrainerListProps {
  branchList: any[];
  branchID: string | null;
  setBranchID: (val: string | null) => void;
  trainersData: any[];
  tableLoader: boolean;
  handleViewDetailsPage: (row: any) => void;
  handleEditModalOpen: (row: any) => void;
  handleDeleteTrainer: (id: string) => void;
  openAddModal: () => void;
  queryTrainerList: (id: string) => void;
}

export default function TrainerList({
  branchList,
  branchID,
  setBranchID,
  trainersData,
  tableLoader,
  handleViewDetailsPage,
  handleEditModalOpen,
  handleDeleteTrainer,
  openAddModal,
  queryTrainerList,
}: TrainerListProps) {
  const breadcrumbslinks = [{ label: "Trainers" }];

  const totalTrainers = trainersData.length;
  const activeTrainers = trainersData.filter(
    (t) => t.status === "Active",
  ).length;
  const inactiveTrainers = totalTrainers - activeTrainers;

  const statsData = [
    {
      title: "Total Trainers",
      count: totalTrainers,
      description: "All registered trainers",
      icon: <IconUsers size={20} color="#EE374D" />,
      bgColor: "#fff5f5", // Light red background
      textColor: "#c92a2a",
      descColor: "#e03131",
    },
    {
      title: "Active Trainers",
      count: activeTrainers,
      description: "Currently active trainers",
      icon: <IconActivity size={20} color="#2b8a3e" />,
      bgColor: "#f0fdf4", // Light red background
      textColor: "#2b8a3e",
      descColor: "#2b8a3e",
    },
    {
      title: "Inactive Trainers",
      count: inactiveTrainers,
      description: "Inactive or suspended",
      icon: <IconAlertCircle size={20} color="#868e96" />,
      bgColor: "#fff5f5",
      textColor: "#868e96",
      descColor: "#868e96",
    },
  ];

  return (
    // <div className="page">
    <Container size="lg" mb={"xl"}>
      {/* Standard Breadcrumbs + Button Header */}
      <Box
        sx={(theme) => ({
          position: "sticky",
          top: 110, // Avoid overlapping the fixed navbar
          zIndex: 100,
          backgroundColor: "#FAFAFA",
          paddingTop: theme.spacing.sm,
          //paddingBottom: theme.spacing.xs,
          marginTop: `-${theme.spacing.sm}`,
        })}
      >
        <SimpleGrid cols={2}>
          <BenchBreadcrumbs links={breadcrumbslinks} />
          <Group position="right">
            <Button
              style={{ backgroundColor: "#ee374d" }}
              leftIcon={<IconPlus size={16} />}
              onClick={openAddModal}
            >
              Add Trainer
            </Button>
          </Group>
        </SimpleGrid>
        <Divider my="sm" />
      </Box>

      {/* Tool Bar - Aligned to standard right dropdowns */}
      {/* Tool Bar - Aligned to standard right dropdowns */}
      <Group position="right" mb="lg">
        <Select
          label="Select Branch"
          placeholder="Select Branch"
          icon={<IconBuildingCommunity size={16} color="#94a3b8" />}
          value={branchID}
          onChange={(val) => {
            if (val) {
              setBranchID(val);
              queryTrainerList(val);
            }
          }}
          data={
            branchList &&
            branchList?.map((item: any) => ({
              value: item.id,
              label:
                item.name ||
                item.branch_name ||
                item.branchName ||
                item.title ||
                "Branch",
            }))
          }
        />
      </Group>

      {/* Statistics Full Width Row */}
      <BenchStatCard data={statsData} />
      {/* Standard Overflow-Hidden Table */}
      <BenchTable>
        <thead>
          <tr style={{ backgroundColor: "#fcfcfc" }}>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Designation</th>
            <th>Status</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {!tableLoader &&
            trainersData.map((row) => (
              <tr
                key={row.id}
                style={{ cursor: "pointer" }}
                onClick={() => handleViewDetailsPage(row)}
              >
                <td style={{ padding: "12px" }}>
                  <Text size="sm" fw={600} color="#1e293b">
                    {row.first_name} {row.last_name}
                  </Text>
                </td>
                <td>
                  <Text size="xs" color="#475569" fw={600}>
                    {row.email || "—"}
                  </Text>
                </td>
                <td>
                  <Text size="xs" color="#475569" fw={600}>
                    {row.mobile_number || "—"}
                  </Text>
                </td>
                <td>
                  <Badge variant="light" size="xs">
                    {row.designation || "Certified Trainer"}
                  </Badge>
                </td>
                <td>
                  <Badge
                    color={row.status === "Active" ? "green" : "gray"}
                    size="xs"
                  >
                    {row.status || "Active"}
                  </Badge>
                </td>
                <td
                  style={{ textAlign: "right" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Menu
                    withinPortal
                    withArrow
                    shadow="md"
                    position="bottom-end"
                  >
                    <Menu.Target>
                      <ActionIcon variant="default">
                        <FcExpand />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item onClick={() => handleViewDetailsPage(row)}>
                        View Profile Details
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item onClick={() => handleEditModalOpen(row)}>
                        Edit
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </td>
              </tr>
            ))}

          {tableLoader && (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "24px" }}>
                <Loader color="red" size="sm" />
              </td>
            </tr>
          )}

          {!tableLoader && trainersData.length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", padding: "24px" }}>
                <Text color="dimmed" size="sm">
                  No trainers found for this branch.
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </BenchTable>
      {/* End of Table Section */}
    </Container>
  );
}
